import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Silence benign 'ResizeObserver loop' errors that CSS `content-visibility: auto`
// can trigger during scroll/resize. These are non-fatal warnings; Chrome fires them
// as error events which CRA's error-overlay otherwise renders as a blocking overlay.
const _isResizeObserverLoopErr = (msg) =>
  typeof msg === "string" && msg.includes("ResizeObserver loop");
const _swallow = (e) => {
  const msg = e?.message || e?.reason?.message || "";
  if (_isResizeObserverLoopErr(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return true;
  }
  return false;
};
window.addEventListener("error", _swallow, true);
window.addEventListener("unhandledrejection", _swallow, true);
// CRA's react-error-overlay hooks window.onerror directly (not via addEventListener),
// so we also intercept the property assignment.
const _origConsoleError = console.error.bind(console);
console.error = (...args) => {
  if (args.length && _isResizeObserverLoopErr(String(args[0]))) return;
  _origConsoleError(...args);
};
// Prevent CRA's error-overlay from installing its own onerror handler for the ROloop case
Object.defineProperty(window, "onerror", {
  configurable: true,
  set(fn) {
    this._patchedOnError = (msg, src, line, col, err) => {
      if (_isResizeObserverLoopErr(msg) || _isResizeObserverLoopErr(err?.message)) return true;
      return typeof fn === "function" ? fn(msg, src, line, col, err) : false;
    };
  },
  get() { return this._patchedOnError; },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
